import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";

export type ProofStandardType = "proof_photo" | "proof_video" | "functional_check";
export type ProofStandardPriority = "normal" | "high_value";

export interface ProofStandard {
  id: string;
  property_id: string;
  checklist_id: string;
  title: string;
  type: ProofStandardType;
  room: string | null;
  /** Checklist room names where this check must be completed (same check in each room). */
  rooms: string[];
  priority: ProofStandardPriority;
  reference_media_urls: string[];
  /** Optional YouTube watch/Shorts/embed link (host demo); cleaners open in browser. */
  reference_youtube_url: string | null;
  cleaner_instructions: string | null;
  required_every_turnover: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface StandardProofUpload {
  id: string;
  submission_id: string;
  cleaning_task_id: string | null;
  standard_id: string;
  proof_media_urls: string[];
  created_at: string;
}

function parseMediaUrls(json: Json | null): string[] {
  if (json == null) return [];
  if (Array.isArray(json)) return json.filter((x): x is string => typeof x === "string");
  return [];
}

function parseRoomNames(json: Json | null, legacyRoom: string | null): string[] {
  if (Array.isArray(json)) {
    const names = json.filter((x): x is string => typeof x === "string" && x.trim().length > 0);
    if (names.length > 0) return names.map((n) => n.trim());
  }
  if (legacyRoom?.trim()) return [legacyRoom.trim()];
  return [];
}

/** Owner: standards for a checklist (Checklist tab) */
export const useProofStandards = (checklistId: string | undefined) => {
  return useQuery({
    queryKey: ["proof-standards", checklistId],
    queryFn: async () => {
      if (!checklistId) return [];
      const { data, error } = await supabase
        .from("proof_standards")
        .select("*")
        .eq("checklist_id", checklistId)
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => {
        const yt = (row as { reference_youtube_url?: unknown }).reference_youtube_url;
        const legacyRoom = row.room;
        const rooms = parseRoomNames(
          (row as { rooms?: Json | null }).rooms ?? null,
          legacyRoom,
        );
        return {
          ...row,
          rooms,
          room: rooms[0] ?? legacyRoom,
          reference_media_urls: parseMediaUrls(row.reference_media_urls),
          reference_youtube_url:
            typeof yt === "string" && yt.trim() ? yt.trim() : null,
        };
      }) as ProofStandard[];
    },
    enabled: !!checklistId,
  });
};

/** Cleaner: standards for the task's checklist (by checklist_id) */
export const useProofStandardsForChecklist = (checklistId: string | undefined) => {
  return useProofStandards(checklistId);
};

/** Proof uploads for a submission (cleaner view + owner evidence) */
export const useStandardProofUploads = (submissionId: string | undefined) => {
  return useQuery({
    queryKey: ["standard-proof-uploads", submissionId],
    queryFn: async () => {
      if (!submissionId) return [];
      const { data, error } = await supabase
        .from("standard_proof_uploads")
        .select("*")
        .eq("submission_id", submissionId);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        proof_media_urls: parseMediaUrls(row.proof_media_urls),
      })) as StandardProofUpload[];
    },
    enabled: !!submissionId,
  });
};

/** Proof uploads by cleaning_task_id (for owner evidence list) */
export const useStandardProofUploadsByTask = (cleaningTaskId: string | undefined) => {
  return useQuery({
    queryKey: ["standard-proof-uploads-by-task", cleaningTaskId],
    queryFn: async () => {
      if (!cleaningTaskId) return [];
      const { data, error } = await supabase
        .from("standard_proof_uploads")
        .select("*")
        .eq("cleaning_task_id", cleaningTaskId);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        proof_media_urls: parseMediaUrls(row.proof_media_urls),
      })) as StandardProofUpload[];
    },
    enabled: !!cleaningTaskId,
  });
};

export const useCreateProofStandard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      property_id: string;
      checklist_id: string;
      title: string;
      type: ProofStandardType;
      room?: string | null;
      rooms?: string[];
      priority?: ProofStandardPriority;
      reference_media_urls?: string[];
      reference_youtube_url?: string | null;
      cleaner_instructions?: string | null;
      required_every_turnover?: boolean;
      display_order?: number;
    }) => {
      const roomList = (args.rooms ?? []).map((r) => r.trim()).filter(Boolean);
      const primaryRoom = roomList[0] ?? args.room ?? null;
      const { data, error } = await supabase
        .from("proof_standards")
        .insert({
          property_id: args.property_id,
          checklist_id: args.checklist_id,
          title: args.title,
          type: args.type ?? "proof_photo",
          room: primaryRoom,
          rooms: roomList as unknown as Json,
          priority: args.priority ?? "normal",
          reference_media_urls: (args.reference_media_urls ?? []) as unknown as Json,
          reference_youtube_url: args.reference_youtube_url ?? null,
        cleaner_instructions: args.cleaner_instructions ?? null,
        required_every_turnover: args.required_every_turnover ?? true,
        display_order: args.display_order ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ProofStandard;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["proof-standards", variables.checklist_id] });
    },
  });
};

export const useUpdateProofStandard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      checklist_id: string;
      title?: string;
      type?: ProofStandardType;
      room?: string | null;
      rooms?: string[];
      priority?: ProofStandardPriority;
      reference_media_urls?: string[];
      reference_youtube_url?: string | null;
      cleaner_instructions?: string | null;
      required_every_turnover?: boolean;
      display_order?: number;
    }) => {
      const { id, checklist_id, ...updates } = args;
      const payload: Record<string, unknown> = {};
      if (updates.title !== undefined) payload.title = updates.title;
      if (updates.type !== undefined) payload.type = updates.type;
      if (updates.rooms !== undefined) {
        const roomList = updates.rooms.map((r) => r.trim()).filter(Boolean);
        payload.rooms = roomList as unknown as Json;
        payload.room = roomList[0] ?? null;
      } else if (updates.room !== undefined) {
        payload.room = updates.room;
        payload.rooms = (updates.room ? [updates.room] : []) as unknown as Json;
      }
      if (updates.priority !== undefined) payload.priority = updates.priority;
      if (updates.reference_media_urls !== undefined) payload.reference_media_urls = updates.reference_media_urls as unknown as Json;
      if (updates.reference_youtube_url !== undefined) payload.reference_youtube_url = updates.reference_youtube_url;
      if (updates.cleaner_instructions !== undefined) payload.cleaner_instructions = updates.cleaner_instructions;
      if (updates.required_every_turnover !== undefined) payload.required_every_turnover = updates.required_every_turnover;
      if (updates.display_order !== undefined) payload.display_order = updates.display_order;
      const { data, error } = await supabase
        .from("proof_standards")
        .update(payload)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as ProofStandard;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["proof-standards", variables.checklist_id] });
    },
  });
};

export const useDeleteProofStandard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, checklist_id }: { id: string; checklist_id: string }) => {
      const { error } = await supabase.from("proof_standards").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["proof-standards", variables.checklist_id] });
    },
  });
};

/** Upsert proof for one standard for a submission (cleaner uploads) */
export const useUpsertStandardProofUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      submission_id: string;
      cleaning_task_id: string | null;
      standard_id: string;
      proof_media_urls: string[];
    }) => {
      const { data, error } = await supabase
        .from("standard_proof_uploads")
        .upsert(
          {
            submission_id: args.submission_id,
            cleaning_task_id: args.cleaning_task_id,
            standard_id: args.standard_id,
            proof_media_urls: args.proof_media_urls as unknown as Json,
          },
          { onConflict: "submission_id,standard_id" }
        )
        .select()
        .single();
      if (error) throw error;
      return data as StandardProofUpload;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["standard-proof-uploads", data.submission_id] });
      queryClient.invalidateQueries({ queryKey: ["standard-proof-uploads-by-task", data.cleaning_task_id] });
    },
  });
};

/** Recommended starter pack (capped at 3). Owner can edit after adding. */
export const RECOMMENDED_STANDARDS: { title: string; type: ProofStandardType; priority: ProofStandardPriority }[] = [
  { title: "TV present", type: "proof_photo", priority: "high_value" },
  { title: "TV powers on", type: "functional_check", priority: "high_value" },
  { title: "Coffee machine present", type: "proof_photo", priority: "high_value" },
];
